import { InvoicedTripsValue } from './constants';
import type { ITripState } from './trip.slice';
import type { TripsListApiArg } from 'src/common/external/bambi-api/tripsApi';

export interface TripFilterDef {
  /** Stable name used for debugging and as a test label. */
  key: string;
  /** True when the user has this filter narrowed beyond its default. */
  isActive: (state: ITripState) => boolean;
  /** Reset this filter on an immer draft of the trip state. */
  reset: (state: ITripState) => void;
  /**
   * Slice of API params this filter contributes to `TripsListApiArg`.
   * Optional because a handful of filters (notably `selectedFilterGroup`)
   * don't have a direct API representation — they're translated into other
   * filters at reducer time.
   */
  toApiParams?: (state: ITripState) => Partial<TripsListApiArg>;
}

function getHasInvoiceValue(v?: InvoicedTripsValue) {
  if (v === InvoicedTripsValue.INVOICED_TRIPS) return true;
  if (v === InvoicedTripsValue.NOT_INVOICED_TRIPS) return false;
  return undefined;
}

function getHasPaidInvoiceValue(v?: InvoicedTripsValue) {
  return v === InvoicedTripsValue.PAID_INVOICE_TRIPS ? true : undefined;
}

function getHasUnpaidInvoiceValue(v?: InvoicedTripsValue) {
  return v === InvoicedTripsValue.UNPAID_INVOICE_TRIPS ? true : undefined;
}

/**
 * Single source of truth for "what counts as a trip filter". Consumers:
 * `selectHasActiveTripFilters`, the `clearFilters` reducer, and
 * `selectFilterParamsFromState` in `usePaginatedTripsListQuery`.
 *
 * The filter-action matcher in `trip.slice.ts` keeps its own list of
 * action creators so that it can use the type-safe `actionCreator.match`
 * API without the circular imports that would result from pulling action
 * creators into this file.
 */
export const TRIP_FILTERS: readonly TripFilterDef[] = [
  {
    key: 'selectedDateRange',
    // The reducer stores an object with undefined fields when the picker is
    // cleared — only treat as active when at least one bound is set.
    isActive: (s) =>
      Boolean(s.selectedDateRange?.from || s.selectedDateRange?.to),
    reset: (s) => {
      s.selectedDateRange = undefined;
    },
    toApiParams: (s) => ({
      rangeStart: s.selectedDateRange?.from || '',
      rangeEnd: s.selectedDateRange?.to || '',
    }),
  },
  {
    key: 'selectedCreatedAtDateRange',
    isActive: (s) =>
      Boolean(
        s.selectedCreatedAtDateRange?.from || s.selectedCreatedAtDateRange?.to
      ),
    reset: (s) => {
      s.selectedCreatedAtDateRange = undefined;
    },
    toApiParams: (s) => ({
      createdAtRangeStart: s.selectedCreatedAtDateRange?.from || '',
      createdAtRangeEnd: s.selectedCreatedAtDateRange?.to || '',
    }),
  },
  {
    key: 'selectedPayers',
    isActive: (s) => s.selectedPayers.length > 0,
    reset: (s) => {
      s.selectedPayers = [];
    },
    toApiParams: (s) => ({ payerIn: s.selectedPayers }),
  },
  {
    key: 'selectedStatuses',
    isActive: (s) => s.selectedStatuses.length > 0,
    reset: (s) => {
      s.selectedStatuses = [];
    },
    toApiParams: (s) => ({ statusIn: s.selectedStatuses }),
  },
  {
    key: 'selectedTags',
    isActive: (s) => s.selectedTags.length > 0,
    reset: (s) => {
      s.selectedTags = [];
    },
    toApiParams: (s) => ({ tags: s.selectedTags.join(',') }),
  },
  {
    key: 'selectedDrivers',
    isActive: (s) => s.selectedDrivers.length > 0,
    reset: (s) => {
      s.selectedDrivers = [];
    },
    toApiParams: (s) => ({ driverIn: s.selectedDrivers }),
  },
  {
    key: 'filterInvoicedTrips',
    isActive: (s) =>
      s.filterInvoicedTrips !== undefined &&
      s.filterInvoicedTrips !== InvoicedTripsValue.ALL_TRIPS,
    reset: (s) => {
      s.filterInvoicedTrips = InvoicedTripsValue.ALL_TRIPS;
    },
    toApiParams: (s) => ({
      hasInvoice: getHasInvoiceValue(s.filterInvoicedTrips),
      hasPaidInvoice: getHasPaidInvoiceValue(s.filterInvoicedTrips),
      hasUnpaidInvoice: getHasUnpaidInvoiceValue(s.filterInvoicedTrips),
    }),
  },
  {
    key: 'filterVehicles',
    isActive: (s) => s.filterVehicles.length > 0,
    reset: (s) => {
      s.filterVehicles = [];
    },
    toApiParams: (s) => ({ vehicles: s.filterVehicles.join(',') }),
  },
  {
    key: 'filterPrice',
    // NumberRangeFilterPopover can apply an empty `{}` / `{min: undefined}`
    // shape, which represents "no price filter" — require an actual numeric
    // bound.
    isActive: (s) =>
      typeof s.filterPrice?.min === 'number' ||
      typeof s.filterPrice?.max === 'number',
    reset: (s) => {
      s.filterPrice = null;
    },
    toApiParams: (s) => ({
      priceMax: s.filterPrice?.max,
      priceMin: s.filterPrice?.min,
    }),
  },
  {
    key: 'filterSpaceTypes',
    isActive: (s) => s.filterSpaceTypes.length > 0,
    reset: (s) => {
      s.filterSpaceTypes = [];
    },
    toApiParams: (s) => ({ spaceType: s.filterSpaceTypes.join(',') }),
  },
  {
    key: 'filterCanceledReasons',
    isActive: (s) => s.filterCanceledReasons.length > 0,
    reset: (s) => {
      s.filterCanceledReasons = [];
    },
    toApiParams: (s) => ({ canceledReasonIn: s.filterCanceledReasons }),
  },
  {
    key: 'filterIncludeNote',
    isActive: (s) => s.filterIncludeNote,
    reset: (s) => {
      s.filterIncludeNote = false;
    },
    toApiParams: (s) => ({ hasDriverEnteredNotes: s.filterIncludeNote }),
  },
  {
    key: 'tripSearchTerm',
    isActive: (s) => s.tripSearchTerm.trim().length > 0,
    reset: (s) => {
      s.tripSearchTerm = '';
    },
    toApiParams: (s) => ({ search: s.tripSearchTerm.trim() }),
  },
  {
    key: 'selectedFilterGroup',
    isActive: (s) =>
      s.selectedFilterGroup !== undefined && s.selectedFilterGroup !== null,
    reset: (s) => {
      s.selectedFilterGroup = undefined;
    },
    // No direct API mapping: setting a filter group populates the other
    // fields via `setSelectedFilterGroup` and they contribute their own
    // API params through the entries above.
  },
];
